import { GameState } from '../core/GameState';
import { bn } from '../core/BigNumber';
import { beautify } from '../utils/format';
import { removeChildren } from '../utils/dom';
import { UpgradeSystem } from '../systems/UpgradeSystem';
import { UpgradeDef } from '../data/upgrades';
import { TooltipSystem } from './TooltipSystem';

export class StoreRenderer {
  private container: HTMLElement;
  private emptyHint: HTMLElement | null;
  private lastRenderedIds: string[] = [];

  private ownedContainer: HTMLElement | null;
  private ownedHeader: HTMLElement | null;
  private ownedCountEl: HTMLElement | null;
  private lastOwnedCount = -1;

  constructor(
    containerId: string,
    private upgradeSystem: UpgradeSystem,
    private tooltipSystem: TooltipSystem,
    private getState: () => GameState,
    private onBuy: (id: string) => void,
  ) {
    const el = document.getElementById(containerId);
    if (!el) throw new Error(`#${containerId} not found`);
    this.container = el;
    this.emptyHint = document.getElementById('upgradesEmpty');

    this.ownedContainer = document.getElementById('ownedUpgrades');
    this.ownedHeader = document.getElementById('ownedHeader');
    this.ownedCountEl = document.getElementById('ownedCount');
    this.initOwnedToggle();
  }

  render(state: GameState): void {
    const available = this.upgradeSystem.getAvailable(state);
    const ids = available.map((d) => d.id);
    const idsChanged = ids.length !== this.lastRenderedIds.length
      || ids.some((id, i) => id !== this.lastRenderedIds[i]);

    if (this.emptyHint) {
      this.emptyHint.style.display = available.length === 0 ? 'block' : 'none';
    }

    if (idsChanged) {
      this.fullRender(state, available);
      this.lastRenderedIds = ids;
      return;
    }

    for (let i = 0; i < available.length; i++) {
      const def = available[i];
      const el = this.container.children[i] as HTMLElement;
      const canAfford = state.cookies.gte(def.price);
      el.classList.toggle('affordable', canAfford);
    }
  }

  private fullRender(state: GameState, available: UpgradeDef[]): void {
    removeChildren(this.container);

    for (const def of available) {
      const el = document.createElement('div');
      el.className = 'upgrade-crate';
      el.dataset.id = def.id;

      const canAfford = state.cookies.gte(def.price);
      el.classList.toggle('affordable', canAfford);
      el.textContent = def.icon;

      this.tooltipSystem.attach(el, () => this.buildTooltip(def));
      el.addEventListener('click', () => this.onBuy(def.id));
      this.container.appendChild(el);
    }
  }

  private buildTooltip(def: UpgradeDef): string {
    const state = this.getState();
    const canAfford = state.cookies.gte(def.price);
    const priceColor = canAfford ? 'color:#6fbf73' : 'color:#f44336';

    let effectLine = '';
    if (def.buildingId) {
      effectLine = `<div style="margin-top:6px;color:#aaa">Doubles <strong>${def.buildingId}</strong> production</div>`;
    } else if (def.power) {
      effectLine = `<div style="margin-top:6px;color:#aaa">Cookie production <strong>+${def.power}%</strong></div>`;
    }

    return `
      <div style="min-width:200px">
        <div style="font-size:15px;font-weight:700;margin-bottom:4px">${def.icon} ${def.name}</div>
        <div style="font-size:13px;color:#ccc;margin-bottom:8px">${def.desc}</div>
        ${effectLine}
        <div style="margin-top:8px;font-size:14px;${priceColor}">
          Cost: <strong>${beautify(bn(def.price))}</strong> cookies
        </div>
      </div>
    `;
  }

  private initOwnedToggle(): void {
    this.ownedHeader?.addEventListener('click', () => {
      this.ownedHeader!.classList.toggle('collapsed');
    });
  }

  renderOwned(state: GameState): void {
    const owned = this.upgradeSystem.getOwned(state);
    const count = owned.length;

    if (this.ownedHeader) {
      this.ownedHeader.style.display = count > 0 ? '' : 'none';
    }
    if (this.ownedCountEl) {
      this.ownedCountEl.textContent = String(count);
    }

    if (count === this.lastOwnedCount) return;
    this.lastOwnedCount = count;

    if (!this.ownedContainer) return;
    removeChildren(this.ownedContainer);

    for (const def of owned) {
      const el = document.createElement('div');
      el.className = 'owned-crate';
      el.textContent = def.icon;
      this.tooltipSystem.attach(el, () => this.buildOwnedTooltip(def));
      this.ownedContainer.appendChild(el);
    }
  }

  private buildOwnedTooltip(def: UpgradeDef): string {
    let effectLine = '';
    if (def.buildingId) {
      effectLine = `<div style="margin-top:6px;color:#aaa">Doubles <strong>${def.buildingId}</strong> production</div>`;
    } else if (def.power) {
      effectLine = `<div style="margin-top:6px;color:#aaa">Cookie production <strong>+${def.power}%</strong></div>`;
    }

    return `
      <div style="min-width:200px">
        <div style="font-size:15px;font-weight:700;margin-bottom:4px">${def.icon} ${def.name}</div>
        <div style="font-size:13px;color:#ccc;margin-bottom:8px">${def.desc}</div>
        ${effectLine}
      </div>
    `;
  }
}
